import { HttpError } from './httpClient';

type TranslateFn = (key: string) => string;

const tryParseJson = (value: string): unknown => {
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return undefined;
  try {
    return JSON.parse(trimmed);
  } catch {
    return undefined;
  }
};

const flattenFieldErrors = (errors: Record<string, unknown>): string | undefined => {
  const messages: string[] = [];
  for (const value of Object.values(errors)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === 'string' && item.trim()) messages.push(item);
      }
    }
  }
  return messages.length > 0 ? messages.join(' ') : undefined;
};

export const formatErrorBody = (data: unknown): string | undefined => {
  if (data == null) return undefined;

  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (!trimmed) return undefined;
    const parsed = tryParseJson(trimmed);
    if (parsed !== undefined) {
      const fromParsed = formatErrorBody(parsed);
      if (fromParsed) return fromParsed;
    }
    return trimmed;
  }

  if (typeof data !== 'object') return undefined;

  const body = data as Record<string, unknown>;

  const envelope = body.Error ?? body.error;
  if (typeof envelope === 'string' && envelope.trim()) {
    const parsed = tryParseJson(envelope);
    if (parsed !== undefined) {
      const fromParsed = formatErrorBody(parsed);
      if (fromParsed) return fromParsed;
    }
    return envelope;
  }

  if (typeof body.message === 'string' && body.message.trim()) return body.message;
  if (typeof body.detail === 'string' && body.detail.trim()) return body.detail;

  if (body.errors && typeof body.errors === 'object') {
    const flat = flattenFieldErrors(body.errors as Record<string, unknown>);
    if (flat) return flat;
  }

  const flat = flattenFieldErrors(body);
  if (flat) return flat;

  if (typeof body.title === 'string' && body.title.trim()) return body.title;

  return undefined;
};

export const extractErrorMessage = (
  error: unknown,
  fallbackKey: string,
  translate: TranslateFn
): string => {
  if (error instanceof HttpError) {
    const fromBody = formatErrorBody(error.data);
    if (fromBody) return fromBody;
    if (error.message && error.message !== error.status.toString()) {
      return error.message;
    }
    return translate(fallbackKey);
  }
  if (error instanceof Error && error.message) return error.message;
  return translate(fallbackKey);
};
